import * as React from 'react';

import { getAuthMode } from 'js/lib/authModeDeepLinkUtils';
import useRouter, { useLocation } from 'js/lib/useRouter';
import { isAuthenticatedUser, get as userGet } from 'js/lib/user';

import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import BypassLogin from 'bundles/enterprise-legacy-learner-home/components/BypassLogin';
import type { NaptimeThirdPartyOrganizationFromSlugFragment } from 'bundles/enterprise-legacy-learner-home/queries/__generated__/EnterpriseProgramBySlugQuery';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';
import { useIsJitOrganization } from 'bundles/unified-common/hooks/useIsJitOrganization';
import useJwtInviteCheck from 'bundles/unified-common/hooks/useJwtInviteCheck';
import useSsoCheck from 'bundles/unified-common/hooks/useSsoCheck';
import { useSsoLockedOutStatus } from 'bundles/unified-common/hooks/useSsoLockedOutStatus';
import { isWithinSsoGracePeriod } from 'bundles/unified-common/utils/ssoGracePeriod';
import useOrgScopedProgramMembership from 'bundles/unified-home-common/hooks/useOrgScopedProgramMembership';

/**
 * EnterpriseIntegrations
 *
 * Owner: Integrations Team
 *
 * Handles any actions for users coming from third-party integrations,
 * namely updating any state or facilitating redirection related to the org's SSO provider.
 */

type JwtInviteAndSSOChecksProps = {
  isUserAuthenticated: boolean;
  thirdPartyOrganization?: NaptimeThirdPartyOrganizationFromSlugFragment;
};

const JwtInviteAndSSOChecks = ({
  isUserAuthenticated,
  thirdPartyOrganization,
  children,
}: React.PropsWithChildren<JwtInviteAndSSOChecksProps>) => {
  const router = useRouter();
  const isInLoginMode = getAuthMode(router.location.query, router.location.hash) === 'login';
  const userId = userGet()?.id;
  const orgScopedProgramMembership = useOrgScopedProgramMembership(userId, thirdPartyOrganization);

  useJwtInviteCheck({
    router,
    programMembership: {
      isInvited: !!orgScopedProgramMembership.isInvitedToAnyProgramInOrg,
      isEmailNotVerified: !orgScopedProgramMembership.isEmailVerified,
    },
    isProgramAdmin: !!orgScopedProgramMembership.isAdminOfAnyProgramInOrg,
    isAuthenticatedUser: isUserAuthenticated,
  });
  useSsoCheck({
    router,
    userId,
    isInLoginMode,
    thirdPartyOrganization,
    isProgramAdmin: !!orgScopedProgramMembership.isAdminOfAnyProgramInOrg,
    isProgramMember: !!orgScopedProgramMembership.isMemberOfAnyProgramInOrg,
    isAuthenticatedUser: isUserAuthenticated,
  });

  return <>{children}</>;
};

export const EnterpriseIntegrations = ({ children }: React.PropsWithChildren<{}>) => {
  const location = useLocation();
  const features = useEnterpriseProductFeatures();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const isUserAuthenticated = isAuthenticatedUser();
  const { isSsoLockedOut: isSsoLockedOutAdminSetting, isLoading: isSsoLockedAdminSettingLoading } =
    useSsoLockedOutStatus(thirdPartyOrganization?.id ?? '');
  const isAttemptSSOLogin = location.query.attemptSSOLogin === 'true';
  const isExternal = location.query.isExternal === 'true';
  const { isJit, isLoading: isJitLoading } = useIsJitOrganization({
    orgId: thirdPartyOrganization?.id,
    skip: !isAttemptSSOLogin || !isUserAuthenticated,
  });
  // ---------------------------------------------------------------------------
  // Redirect to IDP (Identity Provider)
  // ---------------------------------------------------------------------------
  const isOrgWhiteListedForSSOLogin =
    features?.whitelistOrganizationForSSOLogin || features?.whitelistOrganizationProgramsForSSOLogin;
  const isSsoLockedOutEnterprise = features?.enableUnifiedSso
    ? isSsoLockedOutAdminSetting
    : features?.enableForceSsoLogin || features?.enableForceSsoLoginV2Beta;
  const shouldRedirectToIdpOnOrg = isExternal && isSsoLockedOutEnterprise && isOrgWhiteListedForSSOLogin;

  // Only hold render when a redirect decision is actually in play AND the data that decision
  // depends on is still resolving. Ordinary page loads (no isExternal / attemptSSOLogin) never
  // wait, so the page server-side renders. On the server these queries run via getDataFromTree, so
  // when a redirect IS in play its data resolves and the redirect still fires during the markup
  // pass. Org-load and contract-load are intentionally not gated: orgContracts is not read in this
  // component, and a missing org is handled by EnterpriseOrgScopedPageGuard.
  const isResolvingRedirectDecision =
    (isExternal && features?.enableUnifiedSso && isSsoLockedAdminSettingLoading) ||
    (isAttemptSSOLogin && isUserAuthenticated && isJitLoading);

  if (isResolvingRedirectDecision) {
    return null;
  }

  /**
   * When attempting SSO login, always redirect if user is not authenticated,
   * or if it's a JIT organization and the user is not within the SSO grace period
   */
  const shouldRedirectForAttemptSSO =
    isAttemptSSOLogin && (!isUserAuthenticated || (isJit && !isWithinSsoGracePeriod(thirdPartyOrganization?.id ?? '')));
  const shouldRedirectToIdp = shouldRedirectForAttemptSSO || (!isUserAuthenticated && shouldRedirectToIdpOnOrg);

  // SSO/SAML redirect for org-level authentication
  if (shouldRedirectToIdp && thirdPartyOrganization && thirdPartyOrganization.loginMethod === 'SAML') {
    return <BypassLogin thirdPartyOrganizationId={thirdPartyOrganization.id} />;
  }

  // ---------------------------------------------------------------------------
  // Redirect to Login Page
  // ---------------------------------------------------------------------------
  const shouldRedirectToLoginForLfo = !isUserAuthenticated && isExternal && !isAttemptSSOLogin;

  if (shouldRedirectToLoginForLfo) {
    redirectClientOrServer('/login?redirectTo=' + encodeURIComponent(location.pathname + location.search));
    return null;
  }

  return (
    <JwtInviteAndSSOChecks isUserAuthenticated={isUserAuthenticated} thirdPartyOrganization={thirdPartyOrganization}>
      {children}
    </JwtInviteAndSSOChecks>
  );
};

export default EnterpriseIntegrations;
